/* eslint-disable no-nested-ternary */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Popup from 'components/Popup';
import InputText from 'components/Inputs/inputText';
import Button from 'components/Buttons/Button';
import useDialog from 'components/Dialog/useDialog';
import Alert from 'components/Alert';
import InputPermission from 'components/Inputs/InputPermission';
import { TOOL_DATA_ROOM } from 'constants/tools';
import { initPermission } from 'utils/functions/initPermissions';
import { PERMISSION_SCOPES, PERMISSION_TYPES } from 'constants/permissions';
import { createFolder, editFolder } from '../../modules/actions';
import { getFolderName } from '..';

function PopupCreateRename({
  startup,
  literals,
  parent = null,
  onClose,
  setSubfolders = () => { },
  folder,
  rolePath,
}) {
  const editingFolder = typeof folder === 'object';
  const DEFAULT_FOLDER = editingFolder && folder?.related?.type;
  const parentId = parent?.id;

  const canEditParent = parent?.permission?.level > 1;

  const [elemName, setElemName] = useState(DEFAULT_FOLDER ? getFolderName(folder, literals) : (folder?.name || ''));
  const [permission, setPermission] = useState(initPermission(
    folder?.id ? folder : (canEditParent ? parent : null),
    PERMISSION_SCOPES.STARTUP,
    startup,
    TOOL_DATA_ROOM,
  ));
  const [loading, setLoading] = useState(false);
  const { dialog } = useDialog();

  const handleCreateFolder = async () => {
    await createFolder('startup', startup.id, {
      name: elemName, parent: parentId, permission,
    })
      .then((newFolder) => {
        setSubfolders(prev => [newFolder, ...prev]);
        onClose();
      })
      .catch(async (error) => {
        let errorMessage = '';
        if (error?.status === 401) {
          errorMessage = literals.noPermissions;
        }
        await dialog({
          type: 'error',
          text: (
            <>
              <strong>{literals.error}</strong>
              <br />
              {errorMessage}
            </>
          ),
        });
        setLoading(false);
      });
  };

  const handleRenameFolder = async () => {
    await dialog({
      type: 'loading',
      execute: async () => {
        await editFolder(rolePath, startup.id, folder.id, {
          name: elemName, parent: folder.parent, permission,
        })
          .then((newFolder) => {
            setSubfolders(prev => prev.map(cont => (cont.id === newFolder.id ? newFolder : cont)));
            onClose();
          })
          .catch(async (error) => {
            let errorMessage = '';
            if (error?.status === 401) {
              errorMessage = literals.noPermissions;
            }
            await dialog({
              type: 'error',
              text: (
                <>
                  <strong>{literals.error}</strong>
                  <br />
                  {errorMessage}
                </>
              ),
            });
          });
      },
    });
  };

  const handleSubmit = () => {
    setLoading(true);
    if (editingFolder) {
      handleRenameFolder();
    } else {
      handleCreateFolder();
    }
  };

  return (
    <Popup
      title={editingFolder ? literals.editFolder : literals.createNewFolder}
      onClose={onClose}
    >
      {
        DEFAULT_FOLDER ? (
          <Alert type='warning' text={literals.cantEditDefaultFolders} />
        ) : null
      }
      <div className='row'>
        <div className='col-12 col-md-7'>
          <InputText
            preText={literals.folder}
            placeholder={literals.folder}
            onChange={v => setElemName(v)}
            value={elemName}
            isDisabled={DEFAULT_FOLDER}
            isRequired
          />
        </div>
        <div className='col-12 col-md-5'>
          <InputPermission
            scope={{ type: PERMISSION_SCOPES.STARTUP, id: startup.id }}
            element={{ type: PERMISSION_TYPES.FOLDER, id: folder?.id }}
            value={permission}
            onChange={setPermission}
          />
        </div>
      </div>
      {
        !DEFAULT_FOLDER ? (
          <div className='d-flex justify-content-end'>
            <Button
              onClick={handleSubmit}
              text={editingFolder ? literals.editFolder : literals.createFolder}
              disabled={!elemName}
              loading={loading}
            />
          </div>
        ) : null
      }
    </Popup>
  );
}

PopupCreateRename.propTypes = {
  startup: PropTypes.object.isRequired,
  literals: PropTypes.object.isRequired,
  parent: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  setSubfolders: PropTypes.func,
  rolePath: PropTypes.string.isRequired,
  folder: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]).isRequired,
};

export default PopupCreateRename;
